import React, { memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { MenuItem, Select, TextField, Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { OTHER_REJECTION_REASON_VALUE } from 'src/constants/document-review/constants'
import type { ReviewRejectionReason } from 'src/interfaces/documentReview'

interface Props {
  isOpen: boolean
  reasons: ReviewRejectionReason[]
  isSubmitting: boolean
  onConfirm(reasonId: string, reasonMessage: string, reasonDetails: string): void
  onClose(): void
}

const RecollectStepModalComponent = ({
  isOpen,
  reasons,
  isSubmitting,
  onConfirm,
  onClose,
}: Props) => {
  const [reasonId, setReasonId] = useState<string>('')
  const [reasonDetails, setReasonDetails] = useState<string>('')

  const isOther = reasonId === OTHER_REJECTION_REASON_VALUE
  // Other needs the reviewer's own words — the contractor reads this string and "Other" tells
  // them nothing about what to fix.
  const isConfirmDisabled =
    reasonId === '' || (isOther && reasonDetails.trim() === '') || isSubmitting

  const handleConfirm = useCallback(() => {
    const selectedReason = reasons.find((reason) => {
      return reason.id === reasonId
    })

    onConfirm(reasonId, selectedReason?.message ?? '', reasonDetails)
  }, [onConfirm, reasonId, reasonDetails, reasons])

  return (
    <CommonModal
      isOpen={isOpen}
      title="Ask for this document again?"
      text="The contractor is notified and asked to resubmit."
      onClose={onClose}
      buttons={
        <div css={tw`flex flex-row gap-2`}>
          <Button color="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            data-testid="recollect-confirm-button"
          >
            Send back
          </Button>
        </div>
      }
    >
      <div css={tw`flex flex-col gap-4`}>
        <div css={tw`flex flex-col gap-1`}>
          <Typography variant="body2">Reason</Typography>
          <Select
            value={reasonId}
            onChange={(event) => {
              setReasonId(String(event.target.value))
            }}
            displayEmpty
            data-testid="recollect-reason-select"
          >
            <MenuItem value="" disabled>
              Select a reason…
            </MenuItem>
            {reasons.map((reason) => {
              return (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.name}
                </MenuItem>
              )
            })}
            {/* "Other" is a UI-only choice — there is no such row in
                `partner-review-rejection-reason`, so it carries no id. */}
            <MenuItem value={OTHER_REJECTION_REASON_VALUE}>Other</MenuItem>
          </Select>
        </div>
        {isOther ? (
          <TextField
            label="What should the contractor fix?"
            value={reasonDetails}
            onChange={(event) => {
              setReasonDetails(event.target.value)
            }}
            multiline
            minRows={2}
            data-testid="recollect-reason-details"
          />
        ) : null}
      </div>
    </CommonModal>
  )
}

export const RecollectStepModal = memo(RecollectStepModalComponent)
